// Consent-aware PostHog behaviour — cookieless until consent.
//
// PostHog always runs, but what it may store on the device follows the cookie banner:
//   - undecided / rejected → `persistence: "memory"` (nothing written to localStorage or
//     cookies; events are anonymous per-pageload) and session replay off.
//   - accepted → `persistence: "localStorage+cookie"` and session replay on (inputs stay
//     masked — see instrumentation-client.ts).
//   - withdrawal (accepted → cleared/rejected) → replay stops, persistence drops back to
//     memory (posthog-js's update_config clears the old backend, deleting the stored
//     `ph_*` localStorage entry and cookie), and reset() detaches the identity.
//
// Framework-free on purpose: wired from PostHogInit (components/analytics/PostHogInit.tsx),
// a client component that initialises in a useEffect — after hydration commits, not before
// (moved off the old pre-hydration instrumentation-client.ts hook; see that component's doc
// comment for why: PostHog's own async sub-script loading could otherwise race React's
// hydration of the server-rendered tree). The persistence switch still happens synchronously
// with the banner click, outside any React re-render. PostHogInit itself guards against a
// StrictMode double-init; this module has no subscribe-twice risk of its own.

import type { PostHog } from "posthog-js";
import { type ConsentValue, onConsentChange, readConsent } from "@/lib/consent";

interface PosthogConsentContext {
  /** Value of NEXT_PUBLIC_POSTHOG_ENVIRONMENT, re-registered after reset() — reset wipes
   * all super-properties, and losing `environment` would silently untag every later event. */
  environment?: string;
}

/** Init-time options for the current consent state, spread into `posthog.init()` so a
 * visitor who already accepted gets full persistence + replay from the very first event —
 * no cookieless flicker — and everyone else starts (and stays) cookieless. */
export function posthogConsentOptions(consent: ConsentValue | null): {
  persistence: "localStorage+cookie" | "memory";
  disable_session_recording: boolean;
} {
  if (consent === "accepted") {
    return { persistence: "localStorage+cookie", disable_session_recording: false };
  }
  return { persistence: "memory", disable_session_recording: true };
}

/** Super-property mirroring the stored consent choice onto every event, so "why does this
 * session have no replay?" is answerable in PostHog directly instead of by cross-referencing
 * $recording_status in SQL. */
export function consentSuperProperties(consent: ConsentValue | null): {
  cookie_consent: "accepted" | "rejected" | "undecided";
} {
  return { cookie_consent: consent ?? "undecided" };
}

/** Single registration point for both super-properties this module manages. Used at init and
 * after every `reset()` call (which wipes all super-properties) — including the one in
 * UserAvatarMenu's logout handler, outside this module — so no call site can register one
 * property and forget the other. */
export function registerPosthogSuperProperties(
  client: PostHog,
  consent: ConsentValue | null,
  { environment }: PosthogConsentContext = {},
): void {
  client.register({
    ...consentSuperProperties(consent),
    ...(environment ? { environment } : {}),
  });
}

/** Move an already-initialised client between the cookieless and persistent modes. */
export function applyPosthogConsent(
  client: PostHog,
  consent: ConsentValue | null,
  context: PosthogConsentContext = {},
): void {
  if (consent === "accepted") {
    // Order matters: switch persistence first so the recorder that starts next writes its
    // session state to the persistent backend, not the in-memory one it would then lose.
    client.set_config({ persistence: "localStorage+cookie" });
    client.startSessionRecording();
    registerPosthogSuperProperties(client, consent, context);
    return;
  }
  // Withdrawal / rejection: stop recording before touching persistence, then drop to
  // memory — update_config clears the old backend on a persistence change, which is what
  // actually deletes the stored ph_* entry + cookie — then detach the identity.
  client.stopSessionRecording();
  client.set_config({ persistence: "memory" });
  client.reset();
  // reset() wipes all super-properties, so this must happen after it — otherwise later
  // events would silently lose `environment` and/or `cookie_consent`.
  registerPosthogSuperProperties(client, consent, context);
}

/** Subscribe the client to consent changes for the lifetime of the page. Dedupes: only a
 * change in the effective stored value triggers `applyPosthogConsent`. Returns the
 * unsubscribe function (used by tests; production never unsubscribes). */
export function setupPosthogConsent(
  client: PostHog,
  context: PosthogConsentContext = {},
): () => void {
  let lastApplied: ConsentValue | null = readConsent();
  return onConsentChange(() => {
    const current = readConsent();
    if (current === lastApplied) return;
    lastApplied = current;
    applyPosthogConsent(client, current, context);
  });
}
